import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, LifeBuoy, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { categoryLabel, TicketThread, TicketReplyComposer } from "@/pages/Support";

const STATUS_OPTIONS = [
  { k: "open",          label: "Abierto",             cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  { k: "in_progress",   label: "En atención",         cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  { k: "waiting_user",  label: "Esperando usuario",   cls: "bg-purple-500/15 text-purple-600 dark:text-purple-300" },
  { k: "resolved",      label: "Resuelto",            cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  { k: "closed",        label: "Cerrado",             cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
];

export default function AdminSupportTicket() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: async () => (await api.get(`/support/tickets/${ticketId}`)).data,
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });

  const changeStatus = useMutation({
    mutationFn: async (status) => (await api.patch(`/admin/support/tickets/${ticketId}`, { status })).data,
    onSuccess: (_res, status) => {
      toast.success("Estado actualizado");
      qc.setQueryData(["ticket", ticketId], (old) => old ? { ...old, status } : old);
      qc.invalidateQueries({ queryKey: ["admin-support-tickets"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo actualizar el estado"),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }
  if (!ticket) {
    return (
      <div>
        <p className="text-muted-foreground">Ticket no encontrado.</p>
        <button onClick={() => navigate("/admin/support")} className="text-blue-600 mt-2 text-sm">← Volver a la bandeja</button>
      </div>
    );
  }

  const currentStatus = STATUS_OPTIONS.find(s => s.k === ticket.status) || STATUS_OPTIONS[0];
  const ref = "#" + String(ticket.ticket_number).padStart(4, "0");
  const disabled = ticket.status === "closed";

  return (
    <div data-testid="admin-support-ticket-page">
      <div className="flex items-center gap-2 mb-4 text-sm">
        <Link to="/admin/support" data-testid="admin-ticket-back" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition">
          <ArrowLeft className="h-4 w-4" /> Bandeja de soporte
        </Link>
      </div>

      <div className="glass-panel rounded-2xl overflow-hidden shadow-lg">
        <header className="p-5 md:p-6 border-b border-white/30 dark:border-white/10">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="h-11 w-11 rounded-xl bg-blue-500/15 grid place-items-center flex-none">
              <LifeBuoy className="h-5 w-5 text-blue-600 dark:text-blue-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-muted-foreground">{ref}</span>
                <span className={cn("text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full", currentStatus.cls)}
                      data-testid="admin-ticket-status-badge">
                  {currentStatus.label}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {categoryLabel(ticket.category)}
                </span>
              </div>
              <h1 className="font-display text-xl md:text-2xl font-semibold tracking-tight mt-1" data-testid="admin-ticket-subject">
                {ticket.subject}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                <span className="font-medium text-foreground">{ticket.user_name}</span>
                {" · "}
                <a href={`mailto:${ticket.user_email}`} className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:underline">
                  <Mail className="h-3 w-3" /> {ticket.user_email}
                </a>
              </p>
            </div>
            <div className="w-full sm:w-48">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Estado</label>
              <Select value={ticket.status} onValueChange={(v) => changeStatus.mutate(v)}>
                <SelectTrigger data-testid="admin-ticket-status-select" className="mt-1 h-9 bg-white/60 dark:bg-slate-900/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.k} value={s.k} data-testid={`admin-ticket-status-${s.k}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </header>

        <TicketThread ticket={ticket} />

        <TicketReplyComposer
          ticketId={ticketId}
          disabled={disabled}
          endpoint={`/admin/support/tickets/${ticketId}/messages`}
        />
      </div>
    </div>
  );
}
